import z from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { optionNumberSchema, optionSchema } from '~/features/types';

// [MCV-1548] Every field here is a filter, not something the user owes us.
// Declaring them required made useCustomForm's onBlur pass emit zod's raw
// "Invalid input: expected object, received undefined" the moment a filter was
// touched and left, while never gating the search itself. The numeric fields are
// preprocessed because TextInput hands react-hook-form the raw input string, so
// a bare z.number() rejected "" with "expected number, received string".
// `.nullish()` rather than `.optional()`: AsyncSelect is clearable and
// react-select emits null, which `.optional()` still rejects.
// The bounds mirror the API's own query-parameter contract
// (routes/msaas/filter_interfaces.py): fteMin/fteMax are `int` with `ge=1`, and
// contractValueMin/Max are `int` with `ge=0`. A looser client schema does not
// make a wider search work — it just lets handleSubmit pass a value Litestar
// then rejects, so the user gets a failed request instead of results.
const filterInteger = (min: number, minMessage: string) =>
  z.preprocess(
    (arg) => (arg === '' || arg == null ? null : Number(arg)),
    z.number().int(ERROR_MESSAGES.WHOLE_NUMBER).min(min, minMessage).optional().nullable()
  );

const fteFilter = () => filterInteger(1, ERROR_MESSAGES.POSITIVE_INT);
const contractValueFilter = () => filterInteger(0, ERROR_MESSAGES.NON_NEGATIVE_INT);

export const searchAndFilterSchema = z
  .object({
    providerCategory: optionNumberSchema.nullish(),
    aoiSpecialty: optionSchema.nullish(),
    subspecialties: z.array(optionSchema).optional(),
    states: z.array(optionSchema).optional(),
    workLocation: z.array(optionSchema).optional(),
    minFte: fteFilter(),
    maxFte: fteFilter(),
    minContractValue: contractValueFilter(),
    maxContractValue: contractValueFilter(),
    startDate: z.string().optional(),
    sortBy: optionSchema.nullish(),
  })
  // The range inputs carry no label prop, only a shared "FTE Range" /
  // "Contract Value" heading, so the message has to name the fields itself.
  .refine((data) => data.minFte == null || data.maxFte == null || data.minFte <= data.maxFte, {
    message: ERROR_MESSAGES.INVALID_NUMBER_RANGE('Min FTE', 'Max FTE'),
    path: ['maxFte'],
  })
  .refine(
    (data) =>
      data.minContractValue == null ||
      data.maxContractValue == null ||
      data.minContractValue <= data.maxContractValue,
    {
      message: ERROR_MESSAGES.INVALID_NUMBER_RANGE('Min Contract Value', 'Max Contract Value'),
      path: ['maxContractValue'],
    }
  );

export type SearchAndFilterValues = z.infer<typeof searchAndFilterSchema>;
